import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { useLocalParticipant } from "@livekit/components-react";
import { Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import { useCallSessionStore } from "../store/callSessionStore";
import { useCallDuration } from "../hooks/useCallDuration";
import { useCallLayoutTier } from "../hooks/useCallLayoutTier";
import { useAudioOutputControl } from "../hooks/useAudioOutputControl";
import { useInCallDevices } from "../hooks/CallDevicesContext";
import { playCallEnd } from "../lib/sound";
import { recordCallLeft } from "../hooks/useCallActivityRecorder";
import { CircleBtn } from "./CircleBtn";
import { HangUpIcon } from "./HangUpIcon";
import { InCallMicButton } from "./InCallDeviceButtons";

/**
 * Slim pill bar shown bottom-right on every authenticated route OTHER than
 * /call/<active-slug>. Click the meta block to return to the call. Mic /
 * camera / end-call controls give the user the essentials without leaving
 * the current route. The mic's arrow opens the same merged Microphones +
 * Speakers menu as the full call view (bottom sheet on phones, GH-1859),
 * reading and writing the session volume (#1722).
 *
 * Renders nothing when no active session exists — the parent
 * (CallRouteAwareRenderer) only mounts this branch when active is set,
 * but the empty-state guard is defensive in case it's mounted elsewhere
 * in the future.
 */
export function FloatingCallWidget() {
  const active = useCallSessionStore((s) => s.active);
  const clearActive = useCallSessionStore((s) => s.clear);
  const markLeaveIntent = useCallSessionStore((s) => s.markLeaveIntent);
  const navigate = useNavigate();
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } =
    useLocalParticipant();

  // Hooks must be called unconditionally; guard renders below.
  const duration = useCallDuration(active?.joinedAt ?? Date.now());
  const isMobile = useCallLayoutTier() === "mobile";
  const audioOutput = useAudioOutputControl(active?.audioContext);
  const callDevices = useInCallDevices();

  if (!active) return null;

  const handleReturn = () => navigate(`/call/${active.slug}`);
  const handleMic = () => {
    if (!localParticipant) return;
    void localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
  };
  const handleCam = () => {
    if (!localParticipant) return;
    void localParticipant.setCameraEnabled(!isCameraEnabled);
  };
  const handleEnd = () => {
    // Deliberate end from the floating pill — flag intent so the resulting
    // disconnect isn't auto-rejoined (#1178).
    recordCallLeft();
    markLeaveIntent();
    playCallEnd();
    clearActive();
    toast.info("Call ended.");
  };

  // Per-control click handlers stop propagation so they don't bubble to the
  // meta block's navigate handler.
  const stop =
    (handler: () => void) =>
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      handler();
    };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ type: "spring", damping: 26, stiffness: 280 }}
      className="fixed bottom-5 right-5 z-50 max-[480px]:left-3 max-[480px]:right-3 max-[480px]:bottom-3"
    >
      <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-background border border-border shadow-xl">
        {/* Meta block — click target */}
        <div
          role="link"
          tabIndex={0}
          aria-label={`Return to call: ${active.roomName}`}
          onClick={handleReturn}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleReturn();
            }
          }}
          className="flex min-w-0 items-center gap-2 pl-1 pr-3 py-1 rounded-xl cursor-pointer hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span aria-hidden className="size-2 rounded-full bg-emerald-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-500">Live</span>
          <span className="min-w-0 text-sm font-semibold truncate max-w-[180px] text-foreground">
            {active.roomName}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{duration}</span>
        </div>

        {/* Controls */}
        <InCallMicButton
          micOn={isMicrophoneEnabled}
          onToggleMic={handleMic}
          devices={callDevices}
          audio={audioOutput}
          light
          compact
          openUp
          sheet={isMobile}
        />
        <CircleBtn
          size="sm"
          tone={isCameraEnabled ? "neutral" : "danger"}
          onClick={stop(handleCam) as unknown as () => void}
          label={isCameraEnabled ? "Turn off camera" : "Turn on camera"}
          icon={
            isCameraEnabled ? (
              <Video className="size-4" />
            ) : (
              <VideoOff className="size-4" />
            )
          }
        />
        <CircleBtn
          size="sm"
          tone="danger"
          onClick={stop(handleEnd) as unknown as () => void}
          label="End call"
          icon={<HangUpIcon className="size-4" />}
        />
      </div>
    </motion.div>
  );
}
